import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { hasKeyValues } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { About } from '~/modules/about/About';
import { OverviewKeyValues } from '~/modules/overview-key-values/OverviewKeyValues';
import { ThemeKnowledgeGraph } from '~/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { useSnakeMeta } from '~/react-query-hooks/snakes/useSnake';
import { useFlags } from '~/stores/use-flags/useFlags';
import { ChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useAssetOverviewNavigation } from '~/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';
import { BrandsAndProductsSection } from '~/widgets/knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '~/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/CompetitorsSection';
import { GeographicExposureSection } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/GeographicExposureSection';

import * as S from './AssetOverviewContent.styles';

export const AssetOverviewContent = ({ entity }: { entity: Entity }) => {
  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);
  const { navigateToChart } = useAssetOverviewNavigation(entity);
  const isBrandsAndProductsEnabled = useFlags(state =>
    state.getFeatureFlagState('kg_brands_products')
  );

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.ThreeMonths,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  return (
    <>
      <S.StyledEntityHeader
        entity={entity}
        truncateLongTitle
        truncateAtLength={19}
        isDrawer
        headerButton={<></>}
      />
      <S.ContentContainer>
        <S.ChartWrapper>
          <S.StyledChartPrice entity={entity} snakeMeta={snakeMeta} />
          <SimpleChart
            entity={entity}
            initialChartSettings={chartSettings}
            isLegacyVariant={false}
            onClickFullscreen={navigateToChart}
            maxHorizon={RangeHorizon.ThreeYears}
            eventsConfig={{
              TMI: true,
            }}
          />
        </S.ChartWrapper>
        <About entity={entity} isModal showMoreDescription />
        {hasKeyValues(entity.tag) && (
          <S.StyledOverviewKeyValuesWrapper>
            <ErrorBoundaryWrapper>
              <OverviewKeyValues entityTag={entity.tag} />
            </ErrorBoundaryWrapper>
          </S.StyledOverviewKeyValuesWrapper>
        )}

        <ErrorBoundaryWrapper>
          <ThemeKnowledgeGraph entityTag={entity.tag} />
        </ErrorBoundaryWrapper>

        {isBrandsAndProductsEnabled && (
          <ErrorBoundaryWrapper>
            <BrandsAndProductsSection entityTag={entity.tag} />
          </ErrorBoundaryWrapper>
        )}

        <CompetitorsSection entityTag={entity.tag} />
        <ErrorBoundaryWrapper>
          <GeographicExposureSection entityTag={entity.tag} />
        </ErrorBoundaryWrapper>
      </S.ContentContainer>
    </>
  );
};
